/*
 * ModemAudio -- call progress tones and Bell 212A handshake, synthesized,
 * with Hayes speaker control.
 *
 * Every tone is to the North American Precise Tone Plan, so this should
 * sound like a 1986 phone line rather than approximately like one:
 *
 *   dial tone     350 + 440 Hz, continuous
 *   busy          480 + 620 Hz, 0.5s on / 0.5s off
 *   reorder       480 + 620 Hz, 0.25s on / 0.25s off  (fast busy)
 *   ringback      440 + 480 Hz, 2s on / 4s off
 *   DTMF          697/770/852/941 x 1209/1336/1477/1633
 *   answer tone   2225 Hz (Bell 212A)
 *
 * Speaker control follows the Hayes command set:
 *
 * Recorded audio for the 'voice' result is exempt: those clips are story,
 * not line noise, and play regardless of M.
 *
 *   M0  speaker always off
 *   M1  on until carrier detect                        (default)
 *   M2  always on -- including during the data call
 *   M3  off while dialing, on from last digit to carrier detect
 *
 *   L0  minimum      L1  minimum (same as L0 on real hardware)
 *   L2  medium       (default)
 *   L3  high
 *
 * Under M3 the dialing sounds are suppressed but the sequence timing is
 * unchanged, so the result code still lands where it should.
 *
 * Usage:
 *
 *   const audio = new ModemAudio();
 *   audio.attachGesture();               // unlock on first click/key
 *   audio.setSpeaker(1); audio.setLevel(2);
 *   audio.sequence('connect', { digits: '7035550148', targetMs: 9800 });
 */

import { TIME_CLIPS, timeClipBytes } from './timeclips.js';

const DTMF = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477], 'A': [697, 1633],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477], 'B': [770, 1633],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477], 'C': [852, 1633],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477], 'D': [941, 1633],
};

// L0..L3. L0 and L1 are identical on real Hayes hardware.
const LEVEL = [0.35, 0.35, 1.0, 1.9];

// How long the exchange waits for you to dial before it gives up. Real
// timeouts run 15-30 seconds; the exact figure does not matter, only
// that it is finite.
const DIALTONE_TIMEOUT = 22.0;

// Where the speaking-clock recordings live. These are not secret -- the
// digits reveal nothing -- so unlike the answering-machine clips they
// keep readable filenames.
//
// The speaking-clock recordings are compiled into the bundle (see
// tools/build-time-clips.mjs) rather than fetched. A phrase is four or
// five clips end to end, and one of them arriving late turns the
// announcement into a stutter -- a failure that shows up exactly when
// the network is worst. In memory it cannot happen, and the decoded
// buffers can be scheduled on the WebAudio clock, so the joins are
// sample accurate instead of subject to <audio> element timing.
//
// The server still decides which tokens make up the phrase, because the
// server is what knows which recordings exist.

const T = {
  offHook:    0.12,
  dialTone:   0.90,
  dtmfTone:   0.10,
  dtmfGap:    0.06,
  preRing:    0.50,
  ringOn:     2.00,
  ringOff:    1.40,
  answerTone: 1.30,
  scramble:   1.90,
  busyOn:     0.50,
  busyOff:    0.50,
};

export default class ModemAudio {
  /**
   * @param {object} [opts]
   * @param {boolean} [opts.enabled]  global mute, independent of M
   * @param {number} [opts.volume]    master level, 0..1
   * @param {number} [opts.speaker]   initial M setting, 0..3
   * @param {number} [opts.level]     initial L setting, 0..3
   */
  constructor(opts = {}) {
    this.enabled = opts.enabled !== false;
    this.volume = opts.volume ?? 0.25;
    this.speaker = opts.speaker ?? 1;
    this.level = opts.level ?? 2;

    this.ctx = null;
    this.master = null;
    this.active = [];
    this.noiseBuf = null;
    this.voiceEl = null;
    this.voicePending = null;
    this.carrierNodes = null;
    this.speechGen = 0;
    this.speechNodes = [];
    this.speechEnd = null;
    this.clipCache = this.clipCache || null;   // survives a reset: decoding
  }                                            // 49 clips twice is wasteful

  // ------------------------------------------------------------- lifecycle

  /**
   * Wire this up to a card. One call, and every event the card can emit
   * is handled.
   *
   *   const modem = new ModemCard({ endpoint: '/api' });
   *   audio.attach(modem);
   *
   * Replaces writing a switch by hand at the call site. That switch had
   * to list every event name, and each time the card learned a new one
   * -- speaker settings, aborted dials, cutting a recording short -- the
   * card would emit into silence and the feature would look broken with
   * nothing in the console to say why. The card is the thing that knows
   * what it can emit; the mapping belongs next to the code that
   * responds, not scattered across whoever constructs it.
   *
   * Any onEvent the card was built with still runs, after this one.
   */
  attach(card) {
    const prior = card.onEvent;

    card.onEvent = (name, detail) => {
      switch (name) {
        case 'dial':         this.dialTone(); break;
        case 'offhook':      this.dialTone(); break;
        case 'speaker':      this.setSpeaker(detail.speaker);
                             this.setLevel(detail.level); break;
        case 'progress':     this.sequence(detail.kind || 'noanswer', {
                               digits: detail.digits,
                               targetMs: detail.delay,
                               audio: detail.audio,
                             }); break;
        case 'abort':        this.stop(); break;
        case 'hangup':       this.stop(); break;
        case 'stop-voice':   this.stopVoice(); break;
        case 'connect':      this.connected(); break;
        case 'carrier-lost': this.carrierDrop(); break;
        default: break;
      }
      if (typeof prior === 'function') prior(name, detail);
    };

    return this;
  }

  /**
   * Browsers won't start an AudioContext without a user gesture. Call once
   * at startup; it unlocks on the first interaction and detaches.
   */
  attachGesture(target = document) {
    const unlock = () => {
      this._ctx();
      if (this.ctx.state === 'suspended') this.ctx.resume();
      target.removeEventListener('click', unlock);
      target.removeEventListener('keydown', unlock);
      target.removeEventListener('touchend', unlock);
    };
    target.addEventListener('click', unlock);
    target.addEventListener('keydown', unlock);
    target.addEventListener('touchend', unlock);
  }

  _ctx() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this._gain();
      this.master.connect(this.ctx.destination);
    }
    return this.ctx;
  }

  _gain() {
    return this.volume * LEVEL[Math.min(3, Math.max(0, this.level))];
  }

  // --------------------------------------------------------- Hayes M and L

  /** ATM0..ATM3 */
  setSpeaker(m) {
    this.speaker = Math.min(3, Math.max(0, m | 0));
    if (this.speaker === 0) {
      this._stopTones();            // leaves any recording playing
    } else if (this.speaker !== 2) {
      this.onlineCarrier(false);
    }
  }

  /** ATL0..ATL3 */
  setLevel(l) {
    this.level = Math.min(3, Math.max(0, l | 0));
    if (this.master) {
      // Ramp rather than jump, so changing volume mid-tone doesn't click.
      const t = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(t);
      this.master.gain.setValueAtTime(this.master.gain.value, t);
      this.master.gain.linearRampToValueAtTime(this._gain(), t + 0.05);
    }
  }

  /** Developer mute. Independent of M -- use this for testing, not ATM0. */
  setEnabled(on) {
    this.enabled = on;
    if (!on) this.stop();
  }

  setVolume(v) {
    this.volume = v;
    if (this.master) this.master.gain.value = this._gain();
  }

  /**
   * True if the modem's speaker should be producing sound.
   *
   * Note this does NOT gate recorded audio for the 'voice' result. Those
   * clips carry material available nowhere else, so they play
   * whenever sound is on at all, regardless of M. Silencing them
   * would be realistic and would lose content.
   */
  _live() {
    return this.enabled && this.speaker !== 0;
  }

  // ------------------------------------------------------------- sequences

  /**
   * Schedule a whole call.
   *
   * @param {string} kind  'connect'|'busy'|'noanswer'|'reorder'|'voice'|'fax'
   * @param {object} opts
   * @param {string} opts.digits    number to play as DTMF
   * @param {number} opts.targetMs  when the result code will print
   * @param {string|string[]} opts.audio  recording(s) for 'voice'; an array
   *                                      is chosen from at random. Plays
   *                                      regardless of the M setting.
   * @returns {number} scheduled duration in ms
   */
  sequence(kind, opts = {}) {
    this.stop();
    if (!this.enabled) return 0;

    const ctx = this._ctx();
    if (ctx.state === 'suspended') ctx.resume();

    // M gates the tones but never the recording, so M0 still runs the
    // whole schedule -- silently -- and the voice clip lands on time.
    const audible = this.speaker !== 0;

    // M3 mutes the dialing phase only. Timing is computed identically
    // either way so the result code still lands on target.
    const dialing = audible && (this.speaker === 1 || this.speaker === 2);

    const digits = (opts.digits || '').replace(/[^0-9*#]/g, '');
    const target = (opts.targetMs ?? 9800) / 1000;

    const t0 = ctx.currentTime + 0.05;
    let t = t0;

    if (dialing) this._click(t);
    t += T.offHook;

    if (dialing) this._pair(t, T.dialTone, 350, 440);
    t += T.dialTone;

    for (const d of digits) {
      const f = DTMF[d];
      if (!f) continue;
      if (dialing) this._pair(t, T.dtmfTone, f[0], f[1], 0.9);
      t += T.dtmfTone + T.dtmfGap;
    }

    t += T.preRing;

    const remain = Math.max(0, target - (t - t0));

    switch (kind) {
      case 'busy':
      case 'reorder': {
        const on  = kind === 'busy' ? T.busyOn  : T.busyOn / 2;
        const off = kind === 'busy' ? T.busyOff : T.busyOff / 2;
        const n = Math.max(2, Math.floor(remain / (on + off)));
        for (let i = 0; i < n; i++) {
          if (audible) this._pair(t, on, 480, 620);
          t += on + off;
        }
        break;
      }

      case 'connect':
      case 'fax': {
        const tail = T.answerTone + T.scramble;
        const rings = Math.max(1,
          Math.round(Math.max(0, remain - tail) / (T.ringOn + T.ringOff)));
        for (let i = 0; i < rings; i++) {
          if (audible) this._pair(t, T.ringOn, 440, 480);
          t += T.ringOn + T.ringOff;
        }
        // Answering modem picks up: steady 2225 Hz, then both ends
        // scramble and the line turns to noise.
        if (audible) this._tone(t, T.answerTone, 2225, 0.5);
        t += T.answerTone;
        if (audible) this._handshake(t, T.scramble);
        t += T.scramble;
        break;
      }

      case 'voice': {
        const rings = Math.max(1,
          Math.round((remain - 0.4) / (T.ringOn + T.ringOff)));
        for (let i = 0; i < rings; i++) {
          if (audible) this._pair(t, T.ringOn, 440, 480);
          t += T.ringOn + T.ringOff;
        }
        // Always scheduled, even under M0. A voice result with no audio
        // is a phone nobody answers, which is most wrong numbers.
        this._voice(opts.audio, (t - ctx.currentTime) * 1000);
        break;
      }

      case 'time': {
        const rings = Math.max(1,
          Math.round((remain - 0.4) / (T.ringOn + T.ringOff)));
        for (let i = 0; i < rings; i++) {
          if (audible) this._pair(t, T.ringOn, 440, 480);
          t += T.ringOn + T.ringOff;
        }
        const delay = (t - ctx.currentTime) * 1000;
        this.voicePending = setTimeout(() => {
          this.voicePending = null;
          this.speakTime(opts.timeUrl || '/api/time');
        }, delay);
        break;
      }

      case 'noanswer':
      default: {
        const rings = Math.max(2, Math.floor(remain / (T.ringOn + T.ringOff)));
        for (let i = 0; i < rings; i++) {
          if (audible) this._pair(t, T.ringOn, 440, 480);
          t += T.ringOn + T.ringOff;
        }
        break;
      }
    }

    return (t - ctx.currentTime) * 1000;
  }

  /**
   * Called on carrier detect. Under M1 and M3 the speaker cuts out here,
   * which is the whole point of those settings. Under M2 it stays on and
   * you hear the data call for as long as you can stand it.
   */
  connected() {
    if (this.speaker === 2 && this.enabled) {
      this.onlineCarrier(true);
    } else {
      this._stopTones();
    }
  }

  /**
   * Dial tone, as a bounded sequence rather than an open-ended one.
   *
   * An earlier version scheduled an hour of tone and relied on some
   * later event arriving to cut it off. Any missed event -- an aborted
   * dial, a failed fetch, a handler that was never wired up -- left it
   * droning with nothing able to stop it. Now it times out to reorder
   * and then silence, exactly as a real line does when you lift the
   * handset and never dial, so a stuck tone cannot outlive its own
   * timeout no matter what goes wrong upstream.
   */
  dialTone() {
    this._stopTones();                      // never stack two of these
    if (!this._live()) return;
    if (this.speaker === 3) return;         // M3: silent while dialing

    const ctx = this._ctx();
    if (ctx.state === 'suspended') ctx.resume();
    let t = ctx.currentTime;

    this._pair(t, DIALTONE_TIMEOUT, 350, 440);
    t += DIALTONE_TIMEOUT;

    // Off hook too long: the exchange gives up and sends reorder.
    for (let i = 0; i < 8; i++) {
      this._pair(t, 0.25, 480, 620);
      t += 0.5;
    }
  }

  /** Brief carrier hiss, e.g. when a call drops mid-session. */
  carrierDrop() {
    this.onlineCarrier(false);
    if (!this._live()) return;
    this._handshake(this._ctx().currentTime, 0.35, 0.5);
  }

  /**
   * The sound of a live 1200 baud call, for M2. Band-limited noise plus a
   * faint carrier. Deliberately unpleasant -- nobody left M2 on for long.
   */
  onlineCarrier(on) {
    if (!on) {
      if (this.carrierNodes) {
        for (const n of this.carrierNodes) {
          try { n.stop(); } catch (e) { /* already stopped */ }
        }
        this.carrierNodes = null;
      }
      return;
    }
    if (this.carrierNodes || !this._live()) return;

    const ctx = this._ctx();
    const t = ctx.currentTime;

    const g = ctx.createGain();
    g.gain.value = 0.18;
    g.connect(this.master);

    const src = ctx.createBufferSource();
    src.buffer = this._noise();
    src.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1700;
    bp.Q.value = 1.2;
    src.connect(bp);
    bp.connect(g);
    src.start(t);

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 1200;
    const og = ctx.createGain();
    og.gain.value = 0.10;
    osc.connect(og);
    og.connect(g);
    osc.start(t);

    this.carrierNodes = [src, osc];
  }

  /**
   * The speaking clock.
   *
   * Asks the server what time it is on the far end and assembles the
   * answer from digit recordings. The time is NOT computed here: the far
   * end's timezone is the point of the call, and one the caller changes by
   * adjusting their own system clock is not a clue.
   *
   * Every digit but the last uses the rising-intonation take, so the
   * phrase resolves on the final one the way a real announcement does.
   * The whole message plays twice, then the line goes dead.
   *
   * Like the answering-machine clips, this ignores the M setting: it
   * carries content available no other way.
   */
  async speakTime(url = '/api/time') {
    if (!this.enabled) return;

    const gen = ++this.speechGen;

    let report;
    try {
      const r = await fetch(url, { cache: 'no-store' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      report = await r.json();
    } catch (e) {
      // The line is open and nothing is on it -- better than a JS error
      // the caller can neither see nor act on.
      return;
    }
    if (gen !== this.speechGen || !this.enabled) return;

    // Tokens are whole spoken numbers ('14', '30'), not digits: the
    // clock says "fourteen thirty", not "one four three zero".
    const tokens = Array.isArray(report.tokens) ? report.tokens : [];
    if (!tokens.length) return;

    // Rising intonation on every token but the last, so the phrase
    // resolves rather than trailing off.
    const phrase = ['the-time-is'].concat(
      tokens.map((t, i) => (i === tokens.length - 1 ? t : `${t}_`))
    );

    const buffers = await this._decodeClips(phrase);
    if (gen !== this.speechGen || !this.enabled) return;

    const ctx = this._ctx();
    if (ctx.state === 'suspended') await ctx.resume();

    // Speech bypasses the master gain: that node is scaled by the Hayes
    // L setting, and someone who turned the modem speaker down should
    // still hear what the far end says.
    const out = ctx.createGain();
    out.gain.value = Math.min(1, this.volume * 3.2);
    out.connect(ctx.destination);

    const GAP = 0.9;                       // between the two readings
    let t = ctx.currentTime + 0.08;

    for (let rep = 0; rep < 2; rep++) {
      for (const name of phrase) {
        const buf = buffers.get(name);
        if (!buf) continue;                // missing clip: a gap, not a crash
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(out);
        src.start(t);
        this.speechNodes.push(src);
        t += buf.duration;
      }
      if (rep === 0) t += GAP;
    }

    // The far end putting the receiver down, once the reading is over.
    this.speechEnd = this._after((t - ctx.currentTime) * 1000 + 150, () => {
      if (gen === this.speechGen) this._hangupClick();
    });
  }

  /**
   * Decode the clips a phrase needs, caching across calls.
   *
   * decodeAudioData consumes the ArrayBuffer it is given, so each clip is
   * decoded from a fresh copy -- and only once, because the cache holds
   * the resulting AudioBuffer, which is reusable.
   */
  async _decodeClips(names) {
    const ctx = this._ctx();
    if (!this.clipCache) this.clipCache = new Map();

    const wanted = [...new Set(names)];
    await Promise.all(wanted.map(async (name) => {
      if (this.clipCache.has(name)) return;
      const bytes = timeClipBytes(name);
      if (!bytes) {
        this.clipCache.set(name, null);
        return;
      }
      try {
        this.clipCache.set(name, await ctx.decodeAudioData(bytes));
      } catch (e) {
        this.clipCache.set(name, null);
      }
    }));

    return this.clipCache;
  }

  /**
   * Speak an arbitrary list of recording names. Exposed because the
   * number clips are useful beyond the clock -- a countdown, a readback
   * of coordinates, anything the far end should say aloud.
   *
   * @param {string[]} names   e.g. ['0_','7_','3_','0']
   * @param {number} [gap]     seconds of silence before a repeat
   * @param {number} [times]   how many times to read it
   */
  async speak(names, gap = 0.9, times = 1) {
    if (!this.enabled || !names || !names.length) return;
    const gen = ++this.speechGen;

    const buffers = await this._decodeClips(names);
    if (gen !== this.speechGen || !this.enabled) return;

    const ctx = this._ctx();
    if (ctx.state === 'suspended') await ctx.resume();

    const out = ctx.createGain();
    out.gain.value = Math.min(1, this.volume * 3.2);
    out.connect(ctx.destination);

    let t = ctx.currentTime + 0.08;
    for (let r = 0; r < times; r++) {
      for (const name of names) {
        const buf = buffers.get(name);
        if (!buf) continue;
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(out);
        src.start(t);
        this.speechNodes.push(src);
        t += buf.duration;
      }
      if (r < times - 1) t += gap;
    }
    return (t - ctx.currentTime) * 1000;
  }

  /** The far end putting the receiver down. */
  _hangupClick() {
    if (!this.enabled) return;
    const ctx = this._ctx();
    this._click(ctx.currentTime);
    this._pair(ctx.currentTime + 0.25, 0.5, 480, 620);
    this._pair(ctx.currentTime + 1.25, 0.5, 480, 620);
  }

  /**
   * Stop a recording, and only a recording.
   *
   * What a keypress means during a voice call: the operator has put the
   * handset down. Tones are left alone because none should be sounding
   * at that point, and killing them indiscriminately would cut off a
   * ring-out that is still legitimately in progress.
   */
  stopVoice() {
    this.speechGen++;                       // cancel a phrase in flight

    for (const n of this.speechNodes) {
      try { n.stop(); } catch (e) { /* already stopped or never started */ }
    }
    this.speechNodes = [];

    if (this.speechEnd) {
      clearTimeout(this.speechEnd);
      this._timers.delete(this.speechEnd);
      this.speechEnd = null;
    }
    if (this.voicePending) {
      clearTimeout(this.voicePending);
      this.voicePending = null;
    }
    if (this.voiceEl) {
      this.voiceEl.pause();
      this.voiceEl = null;
    }
  }

  /** Kill synthesized tones and the online carrier. Leaves recordings. */
  _stopTones() {
    const now = this.ctx ? this.ctx.currentTime : 0;
    for (const n of this.active) {
      try { n.stop(now); } catch (e) { /* already stopped */ }
    }
    this.active = [];
    this.onlineCarrier(false);
  }

  /** Kill everything, recordings included. */
  stop() {
    this._stopTones();
    this.stopVoice();
  }

  // ------------------------------------------------------------ primitives

  _env(t, dur, peak = 1) {
    const g = this.ctx.createGain();
    // 8ms ramps: no clicks, still crisp enough for DTMF.
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + 0.008);
    g.gain.setValueAtTime(peak, t + dur - 0.008);
    g.gain.linearRampToValueAtTime(0, t + dur);
    g.connect(this.master);
    return g;
  }

  _tone(t, dur, freq, peak = 0.5) {
    const o = this.ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = freq;
    o.connect(this._env(t, dur, peak));
    o.start(t);
    o.stop(t + dur + 0.02);
    this.active.push(o);
    return o;
  }

  _pair(t, dur, f1, f2, peak = 0.5) {
    this._tone(t, dur, f1, peak / 2);
    this._tone(t, dur, f2, peak / 2);
  }

  _click(t) {
    const src = this.ctx.createBufferSource();
    src.buffer = this._noise();
    const bp = this.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1800;
    bp.Q.value = 0.7;
    src.connect(bp);
    bp.connect(this._env(t, 0.035, 0.5));
    src.start(t);
    src.stop(t + 0.05);
    this.active.push(src);
  }

  _noise() {
    if (!this.noiseBuf) {
      const n = this.ctx.sampleRate * 2;
      const buf = this.ctx.createBuffer(1, n, this.ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
      this.noiseBuf = buf;
    }
    return this.noiseBuf;
  }

  /**
   * The screech. Bell 212A carries 1200 bps as differential QPSK on a
   * 1200 Hz carrier; what a human hears is a scrambled bitstream -- a
   * carrier jumping between states plus band-limited noise. Approximated
   * as an oscillator hopping between mark and space at roughly the symbol
   * rate, mixed with bandpassed noise wandering the voiceband.
   */
  _handshake(t, dur, peak = 0.42) {
    const env = this._env(t, dur, peak);

    const osc = this.ctx.createOscillator();
    osc.type = 'square';
    const step = 1 / 600;
    for (let x = 0; x < dur; x += step) {
      osc.frequency.setValueAtTime(Math.random() < 0.5 ? 1200 : 2200, t + x);
    }
    const soften = this.ctx.createBiquadFilter();
    soften.type = 'lowpass';
    soften.frequency.value = 3000;
    osc.connect(soften);
    soften.connect(env);
    osc.start(t);
    osc.stop(t + dur + 0.02);
    this.active.push(osc);

    const src = this.ctx.createBufferSource();
    src.buffer = this._noise();
    src.loop = true;
    const bp = this.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 2.5;
    for (let x = 0; x < dur; x += 0.05) {
      bp.frequency.setValueAtTime(700 + Math.random() * 2200, t + x);
    }
    const ng = this.ctx.createGain();
    ng.gain.value = 0.55;
    src.connect(bp);
    bp.connect(ng);
    ng.connect(env);
    src.start(t);
    src.stop(t + dur + 0.02);
    this.active.push(src);
  }

  /**
   * Recorded audio for the 'voice' result. Accepts a URL or an array to
   * choose from at random. Scheduled with setTimeout rather than
   * AudioContext time because it's an <audio> element -- tens of ms of
   * slop is inaudible after two seconds of ringing.
   */
  _voice(audio, delayMs) {
    if (!audio) return;
    const list = Array.isArray(audio) ? audio : [audio];
    if (!list.length) return;
    const url = list[Math.floor(Math.random() * list.length)];

    this.voicePending = setTimeout(() => {
      this.voicePending = null;
      if (!this.enabled) return;              // dev mute only -- not M
      const el = new Audio(url);
      // Tracks the master volume but not L: an operator who turned the
      // modem speaker down still hears who answered the phone.
      el.volume = Math.min(1, this.volume * 3.2);
      this.voiceEl = el;
      el.play().catch(() => { /* autoplay blocked; no gesture yet */ });
    }, delayMs);
  }
}
